<template>
  <div
    ref="rootRef"
    class="panel-select relative flex w-full"
    :class="[
      $attrs.class,
      `panel-select--${size}`,
      variant !== 'default' ? `panel-select--${variant}` : '',
      { 'panel-select--borderless': !bordered },
    ]"
  >
    <button
      ref="triggerRef"
      type="button"
      :id="inputId"
      :dusk="dusk"
      :disabled="disabled"
      :aria-label="ariaLabel"
      :aria-expanded="isOpen ? 'true' : 'false'"
      class="panel-select__trigger"
      :class="[
        selectClasses,
        {
          'panel-select__trigger--open': isOpen,
          'panel-select__trigger--disabled': disabled,
        },
      ]"
      @click="toggleDropdown"
      @keydown.down.prevent="openDropdown"
      @keydown.up.prevent="openDropdown"
      @keydown.space.prevent="openDropdown"
    >
      <span
        class="panel-select__label min-w-0 truncate"
        :class="{ 'panel-select__placeholder': !selectedOption }"
      >
        {{ selectedLabel }}
      </span>

      <IconArrow
        class="panel-select__chevron pointer-events-none shrink-0 transition-transform"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <teleport to="body">
      <div v-if="isOpen" class="contents">
        <Backdrop :show="true" :style="{ zIndex: 1999 }" @click="closeDropdown" />

        <div
          ref="dropdownRef"
          class="panel-select__dropdown z-[2000] flex flex-col overflow-hidden"
          :style="{ width: dropdownWidth + 'px' }"
          :dusk="dusk ? `${dusk}-dropdown` : undefined"
        >
          <ul
            class="panel-select__options panel-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
            style="max-height: 14rem"
          >
            <li
              v-if="includeEmptyOption"
              :dusk="optionDusk({ value: '' })"
            >
              <button
                type="button"
                class="panel-select__option"
                :class="{
                  'panel-select__option--selected': selected === '' || selected == null,
                  'panel-select__option--disabled': emptyOptionDisabled,
                }"
                :disabled="emptyOptionDisabled"
                @click.stop="chooseOption({ value: '', disabled: emptyOptionDisabled })"
              >
                <span class="min-w-0 truncate">{{ emptyOptionLabel ?? __('—') }}</span>
                <svg
                  v-if="selected === '' || selected == null"
                  class="panel-select__check ms-auto size-4 shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </li>

            <template v-for="(options, group) in groupedOptions" :key="group || 'default'">
              <li
                v-if="group"
                class="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500"
              >
                {{ group }}
              </li>

              <li
                v-for="option in options"
                :key="String(option.value)"
                :dusk="optionDusk(option)"
              >
                <button
                  type="button"
                  class="panel-select__option"
                  :class="{
                    'panel-select__option--selected': isSelected(option),
                    'panel-select__option--disabled': isDisabled(option),
                  }"
                  :disabled="isDisabled(option)"
                  @click.stop="chooseOption(option)"
                >
                  <span class="min-w-0 truncate">{{ labelFor(option) }}</span>
                  <svg
                    v-if="isSelected(option)"
                    class="panel-select__check ms-auto size-4 shrink-0"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </li>
            </template>

            <li
              v-if="!hasVisibleOptions"
              class="px-3 py-6 text-center text-xs text-zinc-500 dark:text-zinc-400"
            >
              {{ __('No results found') }}
            </li>
          </ul>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
import groupBy from 'lodash/groupBy'
import { createPopper } from '@popperjs/core'

export default {
  emits: ['change', 'update:selected'],

  inheritAttrs: false,

  props: {
    options: {
      type: Array,
      default: () => [],
    },
    label: { default: 'label' },
    selected: {},
    size: {
      type: String,
      default: 'md',
      validator: val => ['xxs', 'xs', 'sm', 'md'].includes(val),
    },
    variant: {
      type: String,
      default: 'default',
      validator: val => ['default', 'compact'].includes(val),
    },
    bordered: {
      type: Boolean,
      default: true,
    },
    selectClasses: {
      type: [String, Object, Array],
      default: null,
    },
    includeEmptyOption: {
      type: Boolean,
      default: false,
    },
    emptyOptionLabel: {
      type: String,
      default: null,
    },
    emptyOptionDisabled: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dusk: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    ariaLabel: {
      type: String,
      default: null,
    },
  },

  data: () => ({
    isOpen: false,
    popper: null,
    dropdownWidth: null,
    isRtl: Nova.config('rtlEnabled'),
  }),

  beforeUnmount() {
    this.destroyPopper()
    document.removeEventListener('keydown', this.handleEscape)
  },

  mounted() {
    document.addEventListener('keydown', this.handleEscape)
  },

  methods: {
    labelFor(option) {
      return this.label instanceof Function
        ? this.label(option)
        : option[this.label]
    },

    isSelected(option) {
      return option.value == this.selected
    },

    isDisabled(option) {
      return option.disabled === true
    },

    optionDusk(option) {
      if (!this.dusk) {
        return undefined
      }

      return `${this.dusk}-option-${option.value}`
    },

    handleEscape(event) {
      if (!this.isOpen) {
        return
      }

      if (event.key === 'Escape' || event.key === 'Tab') {
        setTimeout(() => this.closeDropdown(), 50)
      }
    },

    destroyPopper() {
      if (this.popper) {
        this.popper.destroy()
        this.popper = null
      }
    },

    openDropdown() {
      if (this.disabled || this.isOpen) {
        return
      }

      this.isOpen = true
      Nova.$emit('disable-focus-trap')

      this.$nextTick(() => {
        this.dropdownWidth = this.$refs.triggerRef.offsetWidth

        this.destroyPopper()
        this.popper = createPopper(this.$refs.triggerRef, this.$refs.dropdownRef, {
          placement: this.isRtl ? 'bottom-end' : 'bottom-start',
          strategy: 'fixed',
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 6],
              },
            },
          ],
        })
      })
    },

    closeDropdown() {
      if (!this.isOpen) {
        return
      }

      this.isOpen = false
      this.destroyPopper()
      Nova.$emit('enable-focus-trap')
    },

    toggleDropdown() {
      if (this.disabled) {
        return
      }

      if (this.isOpen) {
        this.closeDropdown()
        return
      }

      this.openDropdown()
    },

    chooseOption(option) {
      if (this.isDisabled(option)) {
        return
      }

      this.emitChange(option.value)
      this.closeDropdown()
    },

    emitChange(value) {
      this.$emit('change', value)
      this.$emit('update:selected', value)
    },

    resetSelection() {
      const firstOption = this.listOptions.find(option => !this.isDisabled(option))

      if (firstOption) {
        this.emitChange(firstOption.value)
      }
    },
  },

  computed: {
    inputId() {
      return this.id || undefined
    },

    groupedOptions() {
      return groupBy(this.options, option => option.group || '')
    },

    flatOptions() {
      const options = []

      Object.keys(this.groupedOptions).forEach(group => {
        this.groupedOptions[group].forEach(option => {
          options.push(option)
        })
      })

      return options
    },

    hasVisibleOptions() {
      return this.flatOptions.length > 0 || this.includeEmptyOption
    },

    listOptions() {
      const options = [...this.flatOptions]

      if (!this.includeEmptyOption) {
        return options
      }

      const emptyLabel = this.emptyOptionLabel ?? this.__('—')

      options.unshift({
        value: '',
        label: emptyLabel,
        disabled: this.emptyOptionDisabled,
      })

      return options
    },

    selectedOption() {
      return this.listOptions.find(option => this.isSelected(option)) ?? null
    },

    selectedLabel() {
      if (this.selectedOption) {
        return this.labelFor(this.selectedOption)
      }

      return this.emptyOptionLabel ?? this.__('Choose an option')
    },
  },
}
</script>
